import React, { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { fmtEuro, MONTHS_EL } from "@/lib/format";
import { toast } from "@/components/ui/sonner";
import { Plus, Trash2, Coins, Calculator, Save, X, Printer, FileDown, Users, SlidersHorizontal } from "lucide-react";
import { exportElementToPdf } from "@/lib/pdf";
import TipSlips from "@/components/TipSlips";
import { useAuth } from "@/context/AuthContext";

const uid = () => Math.random().toString(36).slice(2, 9);

const CATEGORIES = [
  { value: "Service", label: "Σερβιτόρος", weight: 5 },
  { value: "Cook", label: "Κουζίνα", weight: 2 },
  { value: "Assistant", label: "Βοηθός / Runner", weight: 1 },
  { value: "Concierge", label: "Concierge", weight: 3 },
  { value: "Spa", label: "Spa", weight: 2 },
  { value: "", label: "—", weight: 1 },
];
const catWeight = (v) => (CATEGORIES.find((c) => c.value === v) || { weight: 1 }).weight; // default fallback map

const blankEntry = () => ({ uid: uid(), employee: "", hours: 0, category: "Service", weight: 5, units: 0 });
const blankPool = () => ({ uid: uid(), label: "Νέο σημείο", amount: 0, mode: "weighted", entries: [blankEntry()] });

// mirror of backend _calc_tips so the UI updates live
function poolCalc(p) {
  const mode = p.mode || "units";
  const rows = (p.entries || []).map((e) => {
    const u = mode === "weighted" ? (Number(e.hours) || 0) * (Number(e.weight) || 0) : Number(e.units) || 0;
    return { ...e, _units: Math.round(u * 100) / 100 };
  });
  const totalUnits = rows.reduce((s, e) => s + e._units, 0);
  const totalHours = rows.reduce((s, e) => s + (Number(e.hours) || 0), 0);
  const unitVal = mode === "percent" ? (Number(p.amount) || 0) / 100 : totalUnits ? (Number(p.amount) || 0) / totalUnits : 0;
  rows.forEach((e) => { e._tips = Math.round(e._units * unitVal * 100) / 100; });
  return { rows, totalUnits: Math.round(totalUnits * 100) / 100, totalHours: Math.round(totalHours * 100) / 100, unitVal };
}

function aggregate(pools) {
  const map = {};
  pools.forEach((p) => {
    const { rows } = poolCalc(p);
    rows.forEach((r) => {
      if (!r.employee) return;
      if (!map[r.employee]) map[r.employee] = { employee: r.employee, tips: 0, breakdown: [] };
      map[r.employee].tips += r._tips;
      map[r.employee].breakdown.push({ label: p.label, units: r._units, tips: r._tips });
    });
  });
  return Object.values(map)
    .map((x) => ({ ...x, tips: Math.round(x.tips * 100) / 100 }))
    .sort((a, b) => b.tips - a.tips);
}

export default function TipsDistribution() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const ownOnly = user?.permissions?.["/tips"] === "O";
  const [name, setName] = useState("");
  const [month, setMonth] = useState(6);
  const [pools, setPools] = useState([
    { uid: uid(), label: "TRIBE Beach Bar", amount: 2233, mode: "weighted", entries: [
      { uid: uid(), employee: "ΒΛΑΔΙΜΗΡΟΣ", hours: 252, category: "Service", weight: 5, units: 0 },
      { uid: uid(), employee: "ΣΙΛΒΑΝΑ", hours: 216, category: "Service", weight: 5, units: 0 },
      { uid: uid(), employee: "ΘΟΔΩΡΗΣ", hours: 216, category: "Cook", weight: 2, units: 0 },
      { uid: uid(), employee: "ΙΝΑ", hours: 216, category: "Assistant", weight: 1, units: 0 },
    ] },
  ]);
  const [slips, setSlips] = useState(null);
  const [serverTotal, setServerTotal] = useState(null);
  const [weights, setWeights] = useState(() => CATEGORIES.reduce((a, c) => (c.value ? { ...a, [c.value]: c.weight } : a), {}));
  const [showWeights, setShowWeights] = useState(false);

  const { data: weightCfg } = useQuery({ queryKey: ["tips-weights"], queryFn: async () => (await api.get("/tips/weights")).data, enabled: !ownOnly });
  useEffect(() => { if (weightCfg?.weights) setWeights((w) => ({ ...w, ...weightCfg.weights })); }, [weightCfg]);

  const { data: saved = [] } = useQuery({ queryKey: ["tips"], queryFn: async () => (await api.get("/tips")).data });
  const { data: employees = [] } = useQuery({ queryKey: ["employees"], queryFn: async () => (await api.get("/employees")).data, enabled: !ownOnly });

  const setPool = (i, patch) => setPools((ps) => ps.map((p, idx) => (idx === i ? { ...p, ...patch } : p)));
  const setEntry = (pi, ei, patch) =>
    setPools((ps) => ps.map((p, idx) => (idx !== pi ? p : { ...p, entries: p.entries.map((e, j) => (j === ei ? { ...e, ...patch } : e)) })));
  const onCategory = (pi, ei, cat) => setEntry(pi, ei, { category: cat, weight: weights[cat] ?? 1 });
  const applyWeightsToAll = () =>
    setPools((ps) => ps.map((p) => ({ ...p, entries: p.entries.map((e) => (e.category && weights[e.category] != null ? { ...e, weight: weights[e.category] } : e)) })));

  const normalize = () =>
    pools.map((p) => ({
      label: p.label,
      amount: Number(p.amount) || 0,
      mode: p.mode || "units",
      entries: p.entries
        .filter((e) => e.employee)
        .map((e) => ({ employee: e.employee, hours: Number(e.hours) || 0, category: e.category || "", weight: Number(e.weight) || 0, units: Number(e.units) || 0 })),
    }));

  const agg = useMemo(() => aggregate(pools), [pools]);
  const grandTotal = useMemo(() => Math.round(pools.reduce((s, p) => s + (Number(p.amount) || 0), 0) * 100) / 100, [pools]);

  const employeeList = (
    <datalist id="tips-employee-list">
      {employees.map((e) => <option key={e.id} value={e.name} />)}
    </datalist>
  );

  const calc = useMutation({
    mutationFn: async () => (await api.post("/tips/calculate", { name: name || "Χωρίς όνομα", month, pools: normalize() })).data,
    onSuccess: (d) => { setServerTotal(d.grand_total); toast.success(`Έλεγχος ΟΚ — σύνολο ${fmtEuro(d.grand_total)}`); },
    onError: (e) => toast.error(apiErr(e)),
  });

  const save = useMutation({
    mutationFn: async () => (await api.post("/tips", { name: name || `Tips ${MONTHS_EL[month]}`, month, pools: normalize() })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["tips"] }); toast.success("Η διανομή αποθηκεύτηκε"); },
    onError: (e) => toast.error(apiErr(e)),
  });

  const saveWeights = useMutation({
    mutationFn: async () => (await api.put("/tips/weights", { weights })).data,
    onSuccess: (d) => { setWeights((w) => ({ ...w, ...d.weights })); applyWeightsToAll(); qc.invalidateQueries({ queryKey: ["tips-weights"] }); toast.success("Οι βαρύτητες αποθηκεύτηκαν & εφαρμόστηκαν παντού"); },
    onError: (e) => toast.error(apiErr(e)),
  });

  useEffect(() => {
    if (slips?.mode === "print") {
      const t = setTimeout(() => { window.print(); setSlips(null); }, 300);
      return () => clearTimeout(t);
    }
    if (slips?.mode === "pdf") {
      const t = setTimeout(async () => {
        const el = document.getElementById("tip-slips");
        if (el) await exportElementToPdf(el, `TAF_Tips_${MONTHS_EL[slips.month]}.pdf`);
        setSlips(null);
      }, 400);
      return () => clearTimeout(t);
    }
  }, [slips]);

  const printSlips = (employeesAgg, m, mode = "print") => setSlips({ employees: employeesAgg, month: m, mode });

  const inp = "w-full rounded-lg border border-[#E3DED4] px-3 py-2 text-sm outline-none focus:border-[#45423C]";
  const cell = "w-full bg-transparent text-right tabular outline-none focus:bg-[#FFFBEB] rounded px-1 py-1 text-sm";

  if (ownOnly) {
    const mine = (saved || []).map((d) => {
      const lines = [];
      (d.pools || []).forEach((p) => (p.entries || []).forEach((e) => {
        lines.push({ label: p.label, amount: Number(e.tips) || 0 });
      }));
      const total = Math.round(lines.reduce((s, l) => s + l.amount, 0) * 100) / 100;
      return { id: d.id, name: d.name, month: d.month, total, lines };
    }).filter((d) => d.lines.length);
    const grand = Math.round(mine.reduce((s, d) => s + d.total, 0) * 100) / 100;
    return (
      <div className="space-y-6" data-testid="tips-page">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-[#C86235]">Service Charge</p>
          <h1 className="font-serif text-3xl md:text-4xl text-[#45423C] mt-1">Τα Tips μου</h1>
          <p className="text-[#6F6A60] text-sm mt-1">Βλέπεις μόνο τα δικά σου tips από τις καταχωρημένες διανομές.</p>
        </div>
        <div className="rounded-2xl bg-white border border-[#E3DED4] p-5 flex items-center justify-between">
          <div className="text-xs uppercase tracking-wider text-[#6F6A60] font-semibold">Σύνολο tips</div>
          <div className="font-serif text-2xl text-[#6E7A63] tabular" data-testid="mytips-grand-total">{fmtEuro(grand)}</div>
        </div>
        {mine.length === 0 && (
          <div className="rounded-2xl bg-white border border-[#E3DED4] p-8 text-center text-[#6F6A60]" data-testid="mytips-empty">
            Δεν υπάρχουν καταχωρημένα tips για εσένα ακόμη.
          </div>
        )}
        {mine.map((d) => (
          <div key={d.id} className="rounded-2xl bg-white border border-[#E3DED4] overflow-hidden" data-testid={`mytips-dist-${d.id}`}>
            <div className="px-5 py-4 border-b border-[#E3DED4] flex items-center justify-between">
              <div className="font-semibold text-[#45423C]">{d.name || `Tips ${MONTHS_EL[d.month] || ""}`}</div>
              <div className="font-serif text-lg text-[#6E7A63] tabular">{fmtEuro(d.total)}</div>
            </div>
            <table className="w-full text-sm">
              <tbody>
                {d.lines.map((l) => (
                  <tr key={l.label} className="border-b border-[#F1EDE6] last:border-0">
                    <td className="px-5 py-2.5 text-[#6F6A60]">{l.label}</td>
                    <td className="px-5 py-2.5 text-right tabular">{fmtEuro(l.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="tips-page">
      {employeeList}
      <div>
        <p className="text-xs font-mono uppercase tracking-widest text-[#C86235]">Service Charge</p>
        <h1 className="font-serif text-3xl md:text-4xl text-[#45423C] mt-1">Διανομή Tips</h1>
        <p className="text-[#6F6A60] text-sm mt-1">Σταθμισμένη διανομή: <b>Μονάδες = Ώρες × Βαρύτητα</b> και <b>Tips = Σύνολο × (Μονάδες ÷ Σ Μονάδων)</b>. Όλα τα πεδία εισόδου είναι επεξεργάσιμα· τα υπόλοιπα υπολογίζονται ζωντανά.</p>
      </div>

      <div className="flex flex-wrap gap-3 items-end no-print">
        <div>
          <label className="text-xs uppercase tracking-wider text-[#6F6A60] font-semibold">Όνομα διανομής</label>
          <input data-testid="tips-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Tips Ιουνίου" className={`${inp} mt-1 w-48`} />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-[#6F6A60] font-semibold">Μήνας</label>
          <select data-testid="tips-month" value={month} onChange={(e) => setMonth(Number(e.target.value))} className={`${inp} mt-1 w-40`}>
            {MONTHS_EL.slice(1).map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
        </div>
        <button data-testid="add-pool-btn" onClick={() => setPools([...pools, blankPool()])} className="inline-flex items-center gap-2 rounded-xl border border-[#E3DED4] bg-white px-4 py-2 text-sm font-medium hover:border-[#B9AE9A]">
          <Plus className="h-4 w-4" /> Προσθήκη σημείου
        </button>
        <button data-testid="toggle-weights-btn" onClick={() => setShowWeights((s) => !s)} className={`inline-flex items-center gap-2 rounded-xl border px-4 py-2 text-sm font-medium ${showWeights ? "border-[#C86235] text-[#C86235] bg-[#C86235]/5" : "border-[#E3DED4] bg-white hover:border-[#B9AE9A]"}`}>
          <SlidersHorizontal className="h-4 w-4" /> Βαρύτητες
        </button>
        <div className="ml-auto text-right">
          <div className="text-xs uppercase tracking-wider text-[#6F6A60] font-semibold">Γενικό σύνολο</div>
          <div className="font-serif text-2xl text-[#6E7A63] tabular" data-testid="tips-grand-total">{fmtEuro(grandTotal)}</div>
        </div>
      </div>

      {showWeights && (
        <div className="rounded-2xl bg-white border border-[#C86235]/30 p-5 no-print" data-testid="weights-panel">
          <h2 className="font-serif text-xl text-[#45423C] inline-flex items-center gap-2"><SlidersHorizontal className="h-5 w-5 text-[#C86235]" /> Ρυθμίσεις Βαρύτητας ανά Κατηγορία</h2>
          <p className="text-[#6F6A60] text-sm mt-1">Ορίστε τη βαρύτητα κάθε κατηγορίας μία φορά. Με την αποθήκευση εφαρμόζεται παντού — σε όλες τις γραμμές και σε νέες επιλογές κατηγορίας.</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mt-4">
            {CATEGORIES.filter((c) => c.value).map((c) => (
              <div key={c.value}>
                <label className="text-xs uppercase tracking-wider text-[#6F6A60] font-semibold block mb-1">{c.label}</label>
                <input type="number" step="0.5" data-testid={`weight-cfg-${c.value}`} value={weights[c.value] ?? ""} onFocus={(e) => e.target.select()}
                  onChange={(e) => setWeights((w) => ({ ...w, [c.value]: e.target.value === "" ? "" : Number(e.target.value) }))}
                  className="w-full rounded-lg border border-[#E3DED4] px-3 py-2 text-sm outline-none focus:border-[#45423C]" />
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-3 mt-4">
            <button data-testid="save-weights-btn" onClick={() => saveWeights.mutate()} disabled={saveWeights.isPending} className="inline-flex items-center gap-2 rounded-xl bg-[#45423C] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#56524A] disabled:opacity-50">
              <Save className="h-4 w-4" /> Αποθήκευση & εφαρμογή παντού
            </button>
            <button data-testid="apply-weights-btn" onClick={applyWeightsToAll} className="inline-flex items-center gap-2 rounded-xl border border-[#E3DED4] bg-white px-5 py-2.5 text-sm font-semibold hover:border-[#B9AE9A]">
              Εφαρμογή στις τρέχουσες γραμμές
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        {pools.map((p, pi) => {
          const mode = p.mode || "units";
          const pc = poolCalc(p);
          const pctOff = mode === "percent" && Math.round(pc.totalUnits) !== 100;
          return (
            <div key={p.uid || p.label} className="rounded-2xl bg-white border border-[#E3DED4] p-5" data-testid={`pool-${pi}`}>
              <div className="flex items-center gap-2">
                <Coins className="h-4 w-4 text-[#8A8577]" />
                <input data-testid={`pool-${pi}-label`} value={p.label} onChange={(e) => setPool(pi, { label: e.target.value })} className="flex-1 font-medium text-[#45423C] bg-transparent outline-none focus:bg-[#FFFBEB] rounded px-1" />
                <button data-testid={`remove-pool-${pi}`} onClick={() => setPools(pools.filter((_, i) => i !== pi))} className="p-1.5 text-[#991B1B] hover:bg-[#FEF2F2] rounded-lg">
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-2">
                <label className="text-xs text-[#6F6A60]">Σύνολο tips €</label>
                <input data-testid={`pool-${pi}-amount`} type="number" value={p.amount} onFocus={(e) => e.target.select()} onChange={(e) => setPool(pi, { amount: e.target.value })} className={`${inp} w-28`} />
                <select data-testid={`pool-${pi}-mode`} value={mode} onChange={(e) => setPool(pi, { mode: e.target.value })} className="rounded-lg border border-[#E3DED4] px-2 py-2 text-xs outline-none focus:border-[#45423C]">
                  <option value="weighted">Ώρες × Βαρύτητα</option>
                  <option value="units">Μονάδες</option>
                  <option value="percent">Ποσοστά %</option>
                </select>
                <span className="text-xs text-[#6F6A60] ml-auto tabular">
                  {mode === "percent"
                    ? <span className={pctOff ? "text-[#991B1B] font-semibold" : ""}>{Math.round(pc.totalUnits * 10) / 10}% κατανεμημένα</span>
                    : <>{pc.totalUnits} μον. · {fmtEuro(pc.unitVal)}/μον.</>}
                </span>
              </div>

              <div className="mt-3 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-wider text-[#8A8577] border-b border-[#E3DED4]">
                      <th className="text-left font-semibold py-1 min-w-[130px]">Υπάλληλος</th>
                      {mode === "weighted" && <th className="text-right font-semibold py-1 w-16">Ώρες</th>}
                      {mode === "weighted" && <th className="text-left font-semibold py-1 w-28">Κατηγορία</th>}
                      {mode === "weighted" && <th className="text-right font-semibold py-1 w-16">Βαρ.</th>}
                      <th className="text-right font-semibold py-1 w-16">{mode === "percent" ? "%" : "Μον."}</th>
                      <th className="text-right font-semibold py-1 w-20">Tips</th>
                      <th className="w-8" />
                    </tr>
                  </thead>
                  <tbody>
                    {pc.rows.map((e, ei) => (
                      <tr key={e.uid || `${pi}-${ei}`} className="border-b border-dashed border-[#EFEADF]" data-testid={`pool-${pi}-row-${ei}`}>
                        <td className="py-1">
                          <input list="tips-employee-list" placeholder="Υπάλληλος" value={e.employee}
                            onChange={(ev) => setEntry(pi, ei, { employee: ev.target.value })}
                            className="w-full bg-transparent outline-none focus:bg-[#FFFBEB] rounded px-1 py-1 text-sm" data-testid={`pool-${pi}-emp-${ei}`} />
                        </td>
                        {mode === "weighted" && (
                          <td className="py-1">
                            <input type="number" value={e.hours} onFocus={(ev) => ev.target.select()}
                              onChange={(ev) => setEntry(pi, ei, { hours: ev.target.value })} className={cell} data-testid={`pool-${pi}-hours-${ei}`} />
                          </td>
                        )}
                        {mode === "weighted" && (
                          <td className="py-1">
                            <select value={e.category || ""} onChange={(ev) => onCategory(pi, ei, ev.target.value)}
                              className="w-full bg-transparent outline-none focus:bg-[#FFFBEB] rounded px-1 py-1 text-xs" data-testid={`pool-${pi}-cat-${ei}`}>
                              {CATEGORIES.map((c) => <option key={c.value || "none"} value={c.value}>{c.label}</option>)}
                            </select>
                          </td>
                        )}
                        {mode === "weighted" && (
                          <td className="py-1">
                            <input type="number" step="0.5" value={e.weight} onFocus={(ev) => ev.target.select()}
                              onChange={(ev) => setEntry(pi, ei, { weight: ev.target.value })} className={cell} data-testid={`pool-${pi}-weight-${ei}`} />
                          </td>
                        )}
                        <td className="py-1">
                          {mode === "weighted" ? (
                            <span className="block text-right tabular text-[#6F6A60] px-1" data-testid={`pool-${pi}-units-${ei}`}>{e._units}</span>
                          ) : (
                            <input type="number" value={e.units} onFocus={(ev) => ev.target.select()}
                              onChange={(ev) => setEntry(pi, ei, { units: ev.target.value })} className={cell} data-testid={`pool-${pi}-units-${ei}`} />
                          )}
                        </td>
                        <td className="py-1 text-right tabular font-medium text-[#6E7A63] px-1" data-testid={`pool-${pi}-tips-${ei}`}>{fmtEuro(e._tips)}</td>
                        <td className="py-1 text-right">
                          <button onClick={() => setPool(pi, { entries: p.entries.filter((_, j) => j !== ei) })} className="p-1 text-[#6F6A60] hover:text-[#991B1B]" data-testid={`pool-${pi}-remove-${ei}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="text-xs font-semibold text-[#45423C]">
                      <td className="py-2">Σύνολα</td>
                      {mode === "weighted" && <td className="py-2 text-right tabular">{pc.totalHours}</td>}
                      {mode === "weighted" && <td />}
                      {mode === "weighted" && <td />}
                      <td className="py-2 text-right tabular">{pc.totalUnits}</td>
                      <td className="py-2 text-right tabular text-[#6E7A63]">{fmtEuro(pc.rows.reduce((s, e) => s + e._tips, 0))}</td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>

              <button onClick={() => setPool(pi, { entries: [...p.entries, { ...blankEntry(), weight: weights["Service"] ?? 5 }] })} className="mt-3 text-xs text-[#C86235] font-medium inline-flex items-center gap-1" data-testid={`pool-${pi}-add-row`}>
                <Plus className="h-3 w-3" /> Προσθήκη ατόμου
              </button>
            </div>
          );
        })}
      </div>

      <div className="flex flex-wrap gap-3 no-print items-center">
        <button data-testid="calc-tips-btn" onClick={() => calc.mutate()} className="inline-flex items-center gap-2 rounded-xl bg-white border border-[#C86235]/40 text-[#C86235] px-5 py-2.5 text-sm font-semibold hover:bg-[#C86235]/5">
          <Calculator className="h-4 w-4" /> Έλεγχος υπολογισμού
        </button>
        <button data-testid="save-tips-btn" onClick={() => save.mutate()} disabled={save.isPending} className="inline-flex items-center gap-2 rounded-xl bg-[#45423C] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#56524A] disabled:opacity-50">
          <Save className="h-4 w-4" /> Αποθήκευση διανομής
        </button>
        {serverTotal !== null && <span className="text-xs text-[#6E7A63]">Server σύνολο: <b className="tabular">{fmtEuro(serverTotal)}</b></span>}
      </div>

      {agg.length > 0 && (
        <div className="rounded-2xl bg-[#EEF0EA] border border-[#6E7A63]/20 p-5" data-testid="tips-aggregate">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 className="font-serif text-xl text-[#6E7A63] inline-flex items-center gap-2"><Users className="h-5 w-5" /> Σύνολο ανά υπάλληλο</h2>
            <div className="flex items-center gap-3">
              <button data-testid="pdf-tip-slips-btn" onClick={() => printSlips(agg, month, "pdf")} className="inline-flex items-center gap-2 rounded-xl bg-white border border-[#6E7A63]/30 text-[#6E7A63] px-3 py-1.5 text-xs font-semibold hover:bg-white/60">
                <FileDown className="h-3.5 w-3.5" /> PDF tips
              </button>
              <button data-testid="print-tip-slips-btn" onClick={() => printSlips(agg, month)} className="inline-flex items-center gap-2 rounded-xl bg-white border border-[#6E7A63]/30 text-[#6E7A63] px-3 py-1.5 text-xs font-semibold hover:bg-white/60">
                <Printer className="h-3.5 w-3.5" /> Αποδείξεις tips
              </button>
              <span className="font-serif text-xl text-[#6E7A63] tabular">{fmtEuro(agg.reduce((s, e) => s + e.tips, 0))}</span>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
            {agg.map((e) => (
              <div key={e.employee} className="rounded-xl bg-white p-4" data-testid={`agg-${e.employee}`}>
                <div className="flex items-center justify-between">
                  <span className="font-medium text-[#45423C]">{e.employee}</span>
                  <span className="font-serif text-lg text-[#6E7A63] tabular">{fmtEuro(e.tips)}</span>
                </div>
                <div className="mt-1 space-y-0.5">
                  {e.breakdown.map((b, bi) => (
                    <div key={`${b.label}-${bi}`} className="flex justify-between text-xs text-[#6F6A60]">
                      <span>{b.label}</span><span className="tabular">{fmtEuro(b.tips)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {saved.length > 0 && (
        <div className="no-print">
          <h2 className="font-serif text-xl text-[#45423C] mb-3">Αποθηκευμένες Διανομές</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {saved.map((s) => (
              <div key={s.id} className="rounded-xl bg-white border border-[#E3DED4] p-4" data-testid={`saved-tip-${s.id}`}>
                <div className="flex items-center justify-between">
                  <span className="font-medium text-[#45423C]">{s.name}</span>
                  <span className="text-xs text-[#6F6A60]">{MONTHS_EL[s.month]}</span>
                </div>
                <div className="font-serif text-lg text-[#6E7A63] tabular mt-1">{fmtEuro(s.grand_total)}</div>
                <div className="flex items-center justify-between mt-1">
                  <div className="text-xs text-[#6F6A60]">{s.pools?.length} σημεία</div>
                  <button onClick={() => printSlips(aggregate(s.pools), s.month)} className="text-xs text-[#C86235] font-medium inline-flex items-center gap-1">
                    <Printer className="h-3 w-3" /> Αποδείξεις
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {slips && <TipSlips name={name || "Tips"} month={slips.month} employees={slips.employees} />}
    </div>
  );
}
